import {
	FC,
	KeyboardEvent,
	SyntheticEvent,
	useEffect,
	useRef,
	useState,
} from "react";

import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import {
	ClickAwayListener,
	Grow,
	IconButton,
	ListItemIcon,
	ListItemText,
	MenuItem,
	MenuList,
	Paper,
	Popper,
	Tooltip,
} from "@mui/material";

import { Translate } from "@/components/Application/Translate";
import { AddComment } from "@/components/Procedures/AddComment";
import { ChecklistStatus } from "@/components/Procedures/Checklist/ChecklistStatus";
import { LiveStepStatus } from "@/components/Procedures/Checklist/LiveStepStatus";
import { getTextColorForBackground } from "@/components/Procedures/Checklist/utils/Helpers";
import { CheckpointType } from "@/components/Procedures/Types/ManualChecklistTypes";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";

interface StepToolsProps {
	checklistId: number | undefined;
	step: ProcedureNodeStep;
	toggleShowMeasurements: () => void;
	toggleShowStepLog: () => void;
	skipStep: () => Promise<void>;
	isActiveStep: boolean;
}

export const StepTools: FC<StepToolsProps> = (props) => {
	const [open, setOpen] = useState(false);
	const [addCommentModalState, setAddCommentModalState] = useState({
		isOpen: false,
		skipComment: false,
	});
	const anchorRef = useRef<HTMLButtonElement>(null);

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event: Event | SyntheticEvent) => {
		if (
			anchorRef.current &&
			anchorRef.current.contains(event.target as HTMLElement)
		) {
			return;
		}

		setOpen(false);
	};

	function handleListKeyDown(event: KeyboardEvent) {
		if (event.key === "Tab") {
			event.preventDefault();
			setOpen(false);
		} else if (event.key === "Escape") {
			setOpen(false);
		}
	}

	const handleSkip = async (e: Event | SyntheticEvent) => {
		handleClose(e);
		// Includes step if it is skipped already
		if (props.step.IsSkipped) {
			await props.skipStep();
		} else {
			// Skip requires a comment. After comment is posted, step will be skipped.
			setAddCommentModalState({ isOpen: true, skipComment: true });
		}
	};

	// return focus to the button when we transitioned from !open -> open
	const prevOpen = useRef(open);
	useEffect(() => {
		if (prevOpen.current && !open) {
			anchorRef.current!.focus();
		}

		prevOpen.current = open;
	}, [open]);

	const showSkip =
		!props.step.Status &&
		(props.step.CheckpointType === CheckpointType.SATOSLive ||
			props.step.CheckpointType === CheckpointType.Checkpoint);

	return (
		<div className="grid-column grid-column--tools">
			<Tooltip
				title={
					<Translate String="Checklist.Step.Advanced">
						Advanced actions
					</Translate>
				}
			>
				<IconButton
					ref={anchorRef}
					id="composition-button"
					aria-controls={open ? "composition-menu" : undefined}
					aria-expanded={open ? "true" : undefined}
					aria-haspopup="true"
					onClick={handleToggle}
					sx={{
						height: "20px",
						width: "20px",
						margin: "-1rem -5px 0 0",
						marginTop: "10px",
					}}
				>
					<MoreVertIcon
						sx={{
							color: props.step.BackgroundColor
								? getTextColorForBackground(props.step.BackgroundColor)
								: "",
						}}
					/>
				</IconButton>
			</Tooltip>
			<Popper
				open={open}
				anchorEl={anchorRef.current}
				role={undefined}
				placement="bottom-end"
				transition
				disablePortal
				sx={{ zIndex: 2 }}
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin:
								placement === "bottom-end" ? "right top" : "right bottom",
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList
									autoFocusItem={open}
									id="composition-menu"
									aria-labelledby="composition-button"
									onKeyDown={handleListKeyDown}
								>
									{showSkip && (
										<MenuItem onClick={handleSkip}>
											<ListItemIcon sx={{ border: "none" }}>
												<SkipNextIcon fontSize="small" />
											</ListItemIcon>
											<ListItemText sx={{ border: "none" }}>
												{props.step.IsSkipped ? (
													<Translate String="Checklist.Tools.Include.Step">
														Include step
													</Translate>
												) : (
													<Translate String="Checklist.Tools.Skip.Step">
														Skip step
													</Translate>
												)}
											</ListItemText>
										</MenuItem>
									)}
									<MenuItem
										disabled={props.step.IsSkipped}
										onClick={(e) => {
											handleClose(e);
											setAddCommentModalState({
												isOpen: true,
												skipComment: false,
											});
										}}
									>
										<ListItemIcon sx={{ border: "none" }}>
											<ChatBubbleIcon fontSize="small" />
										</ListItemIcon>
										{!props.step.Readonly && (
											<ListItemText sx={{ border: "none" }}>
												<Translate String="Checklist.Tools.Add.Comment">
													Add comment
												</Translate>
											</ListItemText>
										)}
									</MenuItem>
									<MenuItem
										onClick={(e) => {
											handleClose(e);
											props.toggleShowStepLog();
										}}
									>
										<ListItemIcon sx={{ border: "none" }}>
											<WorkHistoryIcon fontSize="small" />
										</ListItemIcon>
										<ListItemText sx={{ border: "none" }}>
											<Translate String="Checklist.Tools.Show.Log">
												Show log
											</Translate>
										</ListItemText>
									</MenuItem>
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
			<div className="status-actions">
				<ChecklistStatus checklistId={props.checklistId} step={props.step} />
				<LiveStepStatus
					step={props.step}
					toggleShowMeasurements={props.toggleShowMeasurements}
					isActiveStep={props.isActiveStep}
				/>
				<AddComment
					checklistId={props.checklistId}
					step={props.step}
					showAddComment={addCommentModalState.isOpen}
					closeModal={() =>
						setAddCommentModalState({
							isOpen: false,
							skipComment: false,
						})
					}
					skipComment={addCommentModalState.skipComment}
					skipStep={props.skipStep}
				/>
			</div>
		</div>
	);
};
